import { Flex } from "@chakra-ui/react";
import { MouseEventHandler } from "react";
import { useIntl } from "react-intl";

import { RanaTable } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { TableEmptyUserOrInvite } from "@/components/table-empty-user-or-invite";
import { getPrettyTimeFromNow } from "@/utils/get-pretty-time";

import { RevokeInviteModal } from "./components/revoke-invite-modal";
import { useUserInviteList } from "./hooks/use-user-invite-list";

type UserInviteListProps = {
  openInviteModal: MouseEventHandler<HTMLElement> | undefined;
};

export function UserInviteList({ openInviteModal }: UserInviteListProps) {
  const intl = useIntl();
  const {
    page,
    setPageSize,
    setPage,
    orderings,
    setOrdering,
    userInvitesList,
    isSkeleton,
    inProject,
    numPages,
    limit,
    getTableActions,
    getActions,
    canManage,
    statusses,
    dropdownProps,
    modal,
    onClose,
  } = useUserInviteList();

  return (
    <>
      {modal?.type === "revoke" && (
        <RevokeInviteModal invitations={modal.invitations} onClose={onClose} />
      )}
      <Flex direction="column" mb={4} pt={4}>
        <RanaTable
          emptyElement={<TableEmptyUserOrInvite isInvite={true} onLinkCLick={openInviteModal} />}
          setOrdering={setOrdering}
          ordering={orderings}
          isSkeleton={isSkeleton}
          columnDefinitions={[
            {
              title: intl.formatMessage({
                id: "column.invited_at",
                defaultMessage: "Invited at",
              }),
              orderingField: "created_at",
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({ id: "column.email", defaultMessage: "Email" }),
              width: "table_cell",
            },
            {
              title: inProject
                ? intl.formatMessage({
                    id: "column.project_role",
                    defaultMessage: "Project role",
                  })
                : intl.formatMessage({
                    id: "column.tenant_role",
                    defaultMessage: "Organisation role",
                  }),
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({
                id: "column.status",
                defaultMessage: "Status",
              }),
              orderingField: "status",
              width: "table_cell_xxs",
            },
            {
              title: intl.formatMessage({
                id: "column.expires",
                defaultMessage: "Expires",
              }),
              orderingField: "expires_at",
              width: "table_cell_xxs",
            },
          ]}
          tableActions={getTableActions()}
          tableDataIds={userInvitesList.map((user) => user.id)}
          hasRowActions={canManage}
        >
          <RanaTable.Filters
            dropdowns={[
              {
                label: intl.formatMessage({
                  id: "projects.filter_by_invite_status",
                  defaultMessage: "Filter by invite status",
                }),
                options: [
                  {
                    value: null,
                    label: intl.formatMessage({
                      id: "projects.filter_by_invite_user.all",
                      defaultMessage: "Any status",
                    }),
                  },
                  ...statusses.map((status) => {
                    return {
                      value: status,
                      label: capitalizeFirst(status),
                    };
                  }),
                ],
                ...dropdownProps,
              },
            ]}
          />
          <RanaTable.Container minH="fit">
            <RanaTable.Header />
            <RanaTable.Body>
              {userInvitesList.map((invite) => {
                const { id, email, projectRole, tenantRole, status, createdAt, expiresAt } = invite;
                const role = inProject ? projectRole : tenantRole;

                return (
                  <RanaTable.Row
                    rowActions={canManage ? getActions(invite) : undefined}
                    key={id}
                    rowId={id}
                  >
                    <RanaTable.Cell>
                      {capitalizeFirst(getPrettyTimeFromNow(createdAt))}
                    </RanaTable.Cell>
                    <RanaTable.Cell>{email}</RanaTable.Cell>
                    <RanaTable.Cell>{role && capitalizeFirst(role)}</RanaTable.Cell>
                    <RanaTable.Cell>
                      {status === "pending" && expiresAt.getTime() < Date.now()
                        ? "Expired"
                        : capitalizeFirst(status)}
                    </RanaTable.Cell>
                    <RanaTable.Cell>
                      {status === "pending" && capitalizeFirst(getPrettyTimeFromNow(expiresAt))}
                    </RanaTable.Cell>
                  </RanaTable.Row>
                );
              })}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
        <RanaTable.Pagination
          limit={limit}
          setPageSize={setPageSize}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </Flex>
    </>
  );
}
